import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'src/app/services/employee.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;

export interface TerminationModalEmployee {
    id: number | string;
    fullName?: string;
}

export interface TerminationEvent {
    id: number | string;
    response: any;
}

interface CalendarDay {
    date: Date;
    iso: string;
    day: number;
}

/**
 * Modal reutilizable "Registro de baja" (Dar de baja a un colaborador).
 *
 * Se abre de forma imperativa mediante `open(employee)` desde el componente
 * padre (por ejemplo el listado de colaboradores, o en el futuro el detalle),
 * por lo que basta con incluir `<app-termination-modal>` una sola vez en el
 * template del padre y guardar una referencia con `@ViewChild`.
 */
@Component({
    selector: 'app-termination-modal',
    templateUrl: './termination-modal.component.html',
    styleUrls: ['./termination-modal.component.css']
})
export class TerminationModalComponent implements OnInit {
    /** Permite tener varias instancias del modal en la misma vista sin colisión de IDs. */
    @Input() modalId = 'terminationModal';

    /** Emitido cuando la baja se registró exitosamente en el backend. */
    @Output() terminated = new EventEmitter<TerminationEvent>();

    readonly terminationTypes: string[] = [
        'Voluntaria',
        'Involuntaria',
        'Término de contrato',
        'Jubilación'
    ];

    readonly terminationReasons: string[] = [
        'Mejor oferta laboral (salario más alto, mejores prestaciones)',
        'Cambio de residencia (mudanza a otra ciudad o estado)',
        'Incompatibilidad de horarios (afecta vida personal o familiar)',
        'Falta de crecimiento (no ven un plan de carrera claro)',
        'Condiciones laborales percibidas como desfavorables (cargas excesivas, viajes muy largos, presión de entregas)',
        'Mal clima laboral (conflictos con jefes, compañeros o falta de apoyo)',
        'Fatiga o estrés (exceso de rutas, poco descanso, inseguridad en carreteras)',
        'Proyectos personales (abrir negocio, estudiar, etc.)',
        'Razones de salud personal o familiar',
        'Despido justificado',
        'Despido injustificado',
        'Abandono de empleo',
        'Fin de contrato temporal',
        'Otro'
    ];

    private readonly monthNames = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    readonly weekdayLabels = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'];

    form!: FormGroup;
    submitted = false;

    employeeId: number | string | null = null;
    employeeName = '';

    viewYear = new Date().getFullYear();
    viewMonth = new Date().getMonth();
    calendarWeeks: (CalendarDay | null)[][] = [];
    selectedDays = new Set<string>();

    constructor(
        private fb: FormBuilder,
        private employeeService: EmployeeService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            termination_date: ['', Validators.required],
            termination_type: ['', Validators.required],
            termination_reason: ['', Validators.required],
            total_worked_days: [0],
            comments: ['']
        });
        this.buildCalendar();
    }

    get f() {
        return this.form.controls;
    }

    get monthLabel(): string {
        return `${this.monthNames[this.viewMonth]} ${this.viewYear}`.toUpperCase();
    }

    get isRegisterDisabledLook(): boolean {
        // Refleja visualmente (opacidad) el mismo estado "deshabilitado" del diseño
        // mientras los campos obligatorios no estén completos, sin bloquear el click
        // para poder mostrar los errores de validación inline.
        return this.f['termination_date'].invalid || this.f['termination_type'].invalid || this.f['termination_reason'].invalid;
    }

    /** Abre el modal para el colaborador indicado. Reinicia el formulario y el calendario. */
    open(employee: TerminationModalEmployee): void {
        this.employeeId = employee?.id ?? null;
        this.employeeName = employee?.fullName || '';
        this.submitted = false;
        this.selectedDays.clear();

        const now = new Date();
        this.viewYear = now.getFullYear();
        this.viewMonth = now.getMonth();
        this.buildCalendar();

        this.form.reset({
            termination_date: '',
            termination_type: '',
            termination_reason: '',
            total_worked_days: 0,
            comments: ''
        });

        $(`#${this.modalId}`).modal('show');
    }

    close(): void {
        $(`#${this.modalId}`).modal('hide');
    }

    cancel(): void {
        this.close();
    }

    // ---- Mini calendario ----
    prevMonth(): void {
        this.viewMonth -= 1;
        if (this.viewMonth < 0) {
            this.viewMonth = 11;
            this.viewYear -= 1;
        }
        this.buildCalendar();
    }

    nextMonth(): void {
        this.viewMonth += 1;
        if (this.viewMonth > 11) {
            this.viewMonth = 0;
            this.viewYear += 1;
        }
        this.buildCalendar();
    }

    toggleDay(cell: CalendarDay | null): void {
        if (!cell) return;
        if (this.selectedDays.has(cell.iso)) {
            this.selectedDays.delete(cell.iso);
        } else {
            this.selectedDays.add(cell.iso);
        }
        this.form.get('total_worked_days')?.setValue(this.selectedDays.size);
    }

    isSelected(cell: CalendarDay | null): boolean {
        return !!cell && this.selectedDays.has(cell.iso);
    }

    private buildCalendar(): void {
        const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
        const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
        // getDay(): 0=domingo..6=sábado -> convertimos a 0=lunes..6=domingo
        const firstWeekday = (firstOfMonth.getDay() + 6) % 7;

        const cells: (CalendarDay | null)[] = [];
        for (let i = 0; i < firstWeekday; i++) {
            cells.push(null);
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(this.viewYear, this.viewMonth, day);
            cells.push({ date, iso: this.toIso(date), day });
        }
        while (cells.length % 7 !== 0) {
            cells.push(null);
        }

        const weeks: (CalendarDay | null)[][] = [];
        for (let i = 0; i < cells.length; i += 7) {
            weeks.push(cells.slice(i, i + 7));
        }
        this.calendarWeeks = weeks;
    }

    private toIso(date: Date): string {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    // ---- Confirmación ----
    confirm(): void {
        this.submitted = true;

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        if (!this.employeeId) {
            this.utils.alertSwal({ title: 'Error', text: 'No se identificó al colaborador a dar de baja', icon: 'error' });
            return;
        }

        const raw = this.form.value;
        const payload: any = {
            termination_date: raw.termination_date,
            termination_type: raw.termination_type,
            termination_reason: raw.termination_reason,
            worked_days: Array.from(this.selectedDays).sort(),
            total_worked_days: raw.total_worked_days !== null && raw.total_worked_days !== '' ? Number(raw.total_worked_days) : undefined,
        };
        if (raw.comments) {
            payload.comments = raw.comments;
        }

        this.spinner.show();
        this.employeeService.terminate(this.employeeId, payload).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.close();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'Se ha registrado la baja del colaborador correctamente', icon: 'success' });
                this.terminated.emit({ id: this.employeeId as any, response: resp });
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo registrar la baja del colaborador', icon: 'error' });
            }
        });
    }
}
